import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  Calendar,
  User,
  Share2,
  BookOpen,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { SEO } from '@/components/SEO';
import { Helmet } from '@dr.pogodin/react-helmet';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple markdown renderer for blog content
const MarkdownContent = ({ content }) => {
  if (!content) return null;

  // Process markdown to HTML
  const processMarkdown = (text) => {
    let html = text;
    
    // Skip the first H1 since we already have the title
    // Convert the first # header to nothing (it duplicates the page title)
    html = html.replace(/^# .*\n\n/, '');
    
    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-semibold mt-8 mb-4 text-foreground">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-semibold mt-10 mb-4 text-foreground">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h2 class="text-2xl font-semibold mt-10 mb-4 text-foreground">$1</h2>'); // Convert any remaining H1 to H2
    
    // Bold and italic
    html = html.replace(/\*\*\*(.*?)\*\*\*/g, '<strong><em>$1</em></strong>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Links - internal vs external
    html = html.replace(/\[([^\]]+)\]\(\/([^)]+)\)/g, '<a href="/$2" class="text-primary hover:underline">$1</a>');
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline inline-flex items-center gap-1">$1<svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>');
    
    // Blockquotes
    html = html.replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-primary/30 pl-4 py-2 my-4 text-muted-foreground italic">$1</blockquote>');
    
    // Unordered lists
    html = html.replace(/^\* (.*$)/gim, '<li class="ml-4 mb-2">$1</li>');
    html = html.replace(/(<li.*<\/li>\n?)+/g, '<ul class="list-disc list-inside my-4 space-y-1">$&</ul>');
    
    // Ordered lists
    html = html.replace(/^\d+\. (.*$)/gim, '<li class="ml-4 mb-2">$1</li>');
    
    // Paragraphs (lines not already wrapped)
    html = html.split('\n\n').map(para => {
      if (para.startsWith('<h') || para.startsWith('<ul') || para.startsWith('<ol') || 
          para.startsWith('<blockquote') || para.startsWith('<li') || para.trim() === '') {
        return para;
      }
      return `<p class="mb-4 text-muted-foreground leading-relaxed">${para}</p>`;
    }).join('\n');
    
    // Horizontal rules
    html = html.replace(/^---$/gim, '<hr class="my-8 border-border" />');
    
    return html;
  };

  return (
    <div 
      className="prose prose-slate max-w-none"
      dangerouslySetInnerHTML={{ __html: processMarkdown(content) }}
    />
  );
};

// Article structured data for SEO
const ArticleStructuredData = ({ post }) => {
  if (!post) return null;
  
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.meta_description,
    author: {
      '@type': 'Organization',
      name: post.author || 'GoCompareAI'
    },
    publisher: {
      '@type': 'Organization',
      name: 'GoCompareAI',
      logo: {
        '@type': 'ImageObject',
        url: 'https://gocompareai.com/logo-square.png'
      }
    },
    datePublished: post.published_at,
    dateModified: post.updated_at,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://gocompareai.com/blog/${post.slug}`
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/posts/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Post not found');
          }
          throw new Error('Failed to fetch post');
        }
        const data = await response.json();
        setPost(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.meta_description,
          url: window.location.href
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SEO 
          title="Post Not Found | GoCompareAI"
          description="The requested blog post could not be found."
          noindex={true}
        />
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md mx-4">
            <CardContent className="text-center py-12">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h1 className="text-xl font-semibold mb-2">Post Not Found</h1>
              <p className="text-muted-foreground mb-6">
                The blog post you're looking for doesn't exist or has been removed.
              </p>
              <Button asChild>
                <Link to="/blog">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Blog
                </Link>
              </Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title={post.seo_title || `${post.title} | GoCompareAI`}
        description={post.meta_description}
        keywords={post.tags?.join(', ')}
        type="article"
      />
      <ArticleStructuredData post={post} />
      <Header />
      
      <main className="flex-1 w-full">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {/* Back navigation */}
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-6 -ml-2 gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Button>

          {/* Article Header */}
          <header className="mb-8">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map(tag => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Title - H1 */}
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight">
              {post.title}
            </h1>
            
            {/* Meta information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.reading_time} min read
              </span>
              <span className="flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="gap-1.5 h-auto py-1"
                onClick={handleShare}
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          </header>
          
          <Separator className="mb-8" />
          
          {/* Article Content */}
          <div className="article-content">
            <MarkdownContent content={post.content} />
          </div>
          
          <Separator className="my-8" />
          
          {/* Footer CTA */}
          <Card className="bg-muted/50">
            <CardContent className="py-8 text-center">
              <h3 className="text-lg font-medium mb-2">Ready to compare AI platforms?</h3>
              <p className="text-muted-foreground mb-4">
                Use our free guide to find the right AI tool for your needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild>
                  <Link to="/guide">
                    Start the Guide
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/compare">
                    Compare Platforms
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </article>
      </main>
      
      <Footer />
    </div>
  );
}
